import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import heic2any from 'heic2any';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import UniversitySearch from '../../components/UniversitySearch/UniversitySearch';
import RangeCalendar from '../../components/RangeCalendar/RangeCalendar';
import { SUPPORTED_UNIVERSITY_IDS } from '../../config/supportedUniversities';
import './Create.css';

interface FormData {
    title: string;
    description: string;
    unit_type: string;
    address: string;
    monthly_rent: string;
    pictures: string[];
    lease_term: string[];
    lease_start_date: string;
    lease_end_date: string;
    university_id: string;
    parking: boolean;
    allow_pets: boolean;
    gym: boolean;
    pool: boolean;
    laundry: boolean;
    gender_preference: string;
    facebook_url?: string;
    instagram_username?: string;
}

const Create: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated, user, refreshUserProfile } = useAuth();
    const { isDarkMode } = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [uploadedImages, setUploadedImages] = useState<File[]>([]);
    const [uploadedImagePreviews, setUploadedImagePreviews] = useState<string[]>([]);
    const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
    const [isAddressValid, setIsAddressValid] = useState(false); // Track if address is valid (selected from suggestions)
    const addressInputRef = useRef<HTMLDivElement>(null); // Ref for scrolling to address field
    const submitButtonRef = useRef<HTMLDivElement>(null); // Ref for scrolling to submit button

    // Validation + scroll targeting
    const titleRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const universityRef = useRef<HTMLDivElement>(null);
    const leaseTermRef = useRef<HTMLDivElement>(null);
    const unitTypeRef = useRef<HTMLDivElement>(null);
    const rentRef = useRef<HTMLDivElement>(null);
    const photosRef = useRef<HTMLDivElement>(null);
    const utilitiesRef = useRef<HTMLDivElement>(null);
    const genderPreferenceRef = useRef<HTMLDivElement>(null);
    const leaseDatesRef = useRef<HTMLDivElement>(null);


    const [formData, setFormData] = useState<FormData>({
        title: '',
        description: '',
        unit_type: '',
        address: '',
        monthly_rent: '',
        pictures: [],
        lease_term: [],
        lease_start_date: '',
        lease_end_date: '',
        university_id: '',
        parking: false,
        allow_pets: false,
        gym: false,
        pool: false,
        laundry: false,
        gender_preference: 'no_preference',
        facebook_url: '',
        instagram_username: ''
    });


    // Cleanup object URLs when component unmounts or images change
    useEffect(() => {
        return () => {
            uploadedImagePreviews.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [uploadedImagePreviews]);

    // Auto-resize description textarea
    useEffect(() => {
        const textarea = document.getElementById('description') as HTMLTextAreaElement;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${Math.max(100, textarea.scrollHeight)}px`;
        }
    }, [formData.description]);


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
            return newData;
        });
    };

    const handleLeaseTermChange = (term: string) => {
        setFormData(prev => {
            const currentTerms = prev.lease_term;
            const isSelected = currentTerms.includes(term);

            if (isSelected) {
                // Remove term if already selected
                return {
                    ...prev,
                    lease_term: currentTerms.filter(t => t !== term)
                };
            } else {
                // Add term if not selected
                return {
                    ...prev,
                    lease_term: [...currentTerms, term]
                };
            }
        });
    };

    const compressImage = async (file: File, maxSizeKB: number = 500): Promise<File> => {
        let fileToProcess = file;
        
        // Convert HEIC/HEIF to JPEG
        const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                       file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
        
        if (isHeic) {
            try {
                const convertedBlob = await heic2any({
                    blob: file,
                    toType: 'image/jpeg',
                    quality: 0.8
                });
                const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
                fileToProcess = new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                });
            } catch (error) {
                console.error("Error converting HEIC:", error);
                // Fall back to original file if conversion fails
            }
        }

        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions (max 1920x1080)
                let { width, height } = img;
                const maxWidth = 1920;
                const maxHeight = 1080;

                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width *= ratio;
                    height *= ratio;
                }

                canvas.width = width;
                canvas.height = height;

                // Draw and compress
                ctx?.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    if (blob) {
                        const compressedFile = new File([blob], fileToProcess.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        });
                        resolve(compressedFile);
                    } else {
                        resolve(fileToProcess);
                    }
                }, 'image/jpeg', 0.8); // 80% quality
            };

            img.onerror = () => {
                // If image fails to load (e.g., HEIC on unsupported browsers), just resolve with original
                console.warn(`Failed to decode image for compression: ${fileToProcess.name}. Proceeding with original file.`);
                resolve(fileToProcess);
            };

            img.src = URL.createObjectURL(fileToProcess);
        });
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);

        // Detect mobile device for better error messages
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (uploadedImages.length + files.length > 20) {
            setError('Maximum 20 photos allowed');
            return;
        }

        // Validate file types - be more lenient for mobile devices
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/tiff', 'image/bmp', 'image/gif'];
        const invalidFiles = files.filter(file => {
            // For mobile devices, also check file extension if MIME type is not recognized
            if (!validTypes.includes(file.type)) {
                if (isMobile && file.name && file.name.includes('.')) {
                    const ext = file.name.split('.').pop()?.toLowerCase();
                    const validExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'tiff', 'bmp', 'gif'];
                    return !validExtensions.includes(ext || '');
                }
                return true;
            }
            return false;
        });

        if (invalidFiles.length > 0) {
            console.error('Invalid file types:', invalidFiles.map(f => ({ name: f.name, type: f.type })));
            const errorMessage = isMobile
                ? 'Please select valid image files. Supported formats: JPEG, PNG, WebP, HEIC, HEIF, TIFF, BMP, GIF'
                : 'Only JPEG, PNG, WebP, HEIC, HEIF, TIFF, and BMP images are allowed';
            setError(errorMessage);
            return;
        }

        // Validate file sizes (max 10MB per file before processing)
        const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            console.error('Oversized files:', oversizedFiles.map(f => ({ name: f.name, size: f.size })));
            setError('Each image must be less than 10MB');
            return;
        }

        // Additional validation for empty files
        const emptyFiles = files.filter(file => file.size === 0);
        if (emptyFiles.length > 0) {
            console.error('Empty files detected:', emptyFiles.map(f => ({ name: f.name, size: f.size })));
            setError('One or more files appear to be empty. Please select valid image files.');
            return;
        }

        // Compress images before adding to state
        try {
            setError('Compressing images...');
            const compressedFiles: File[] = [];
            for (const file of files) {
                try {
                    const compressed = await compressImage(file);
                    compressedFiles.push(compressed);
                } catch (err) {
                    console.error('Error compressing a file:', err);
                    compressedFiles.push(file); // Fallback to original
                }
            }

            console.log('Original files:', files.map(f => ({ name: f.name, size: f.size })));
            console.log('Compressed files:', compressedFiles.map(f => ({ name: f.name, size: f.size })));

            setUploadedImages(prev => [...prev, ...compressedFiles]);
            setUploadedImagePreviews(prev => [
                ...prev,
                ...compressedFiles.map((file) => URL.createObjectURL(file)),
            ]);
            setError('');
        } catch (error) {
            console.error('Image compression error:', error);
            setError('Failed to process images. Please try again.');
            return;
        }

        // Reset the file input to prevent issues on Windows
        e.target.value = '';
    };

    const removeImage = (index: number) => {
        setUploadedImagePreviews(prev => {
            const urlToRevoke = prev[index];
            if (urlToRevoke) {
                URL.revokeObjectURL(urlToRevoke);
            }
            return prev.filter((_, i) => i !== index);
        });
        setUploadedImages(prev => {
            return prev.filter((_, i) => i !== index);
        });
        setPrimaryImageIndex((prev) => {
            if (prev === index) return 0;
            if (prev > index) return prev - 1;
            return prev;
        });
    };

    // Primary = selected tile (no reordering in UI).
    const setPrimaryImage = (index: number) => {
        setPrimaryImageIndex(index);
    };

    const validateToken = async (token: string): Promise<boolean> => {
        try {
            const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.ok;
        } catch (error) {
            console.error('Token validation error:', error);
            return false;
        }
    };

    const uploadImagesToServer = async (images: File[], retryCount = 0): Promise<{ urls: string[], thumbnailUrls: string[] }> => {
        const maxRetries = 2;

        try {
            console.log(`Starting image upload process... (attempt ${retryCount + 1}/${maxRetries + 1})`);
            console.log('Number of images to upload:', images.length);
            console.log('Upload endpoint:', API_ENDPOINTS.UPLOAD.MULTIPLE_BASE64);

            // Validate token before attempting upload
            const authToken = localStorage.getItem('auth_token');
            if (!authToken) {
                throw new Error('No authentication token found. Please log in again.');
            }

            const isTokenValid = await validateToken(authToken);
            if (!isTokenValid) {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user_data');
                throw new Error('Your session has expired. Please log in again.');
            }

            // Convert images to base64 (same logic as profile upload)
            console.log('Converting images to base64...');
            const base64Images = await Promise.all(
                images.map(async (image) => {
                    const base64Data = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            const result = reader.result as string;
                            resolve(result);
                        };
                        reader.onerror = () => reject(reader.error);
                        reader.readAsDataURL(image);
                    });

                    return {
                        filename: image.name,
                        content_type: image.type,
                        image_data: base64Data
                    };
                })
            );

            console.log('Images converted to base64, uploading sequentially to prevent payload limits...');
            const allUrls: string[] = [];
            const allThumbnailUrls: string[] = [];

            for (let i = 0; i < base64Images.length; i++) {
                console.log(`Uploading image ${i + 1} of ${base64Images.length}...`);
                const response = await fetch(API_ENDPOINTS.UPLOAD.MULTIPLE_BASE64, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        images: [base64Images[i]]
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || `Upload failed with status ${response.status}`);
                }

                const data = await response.json();

                if (data.failed_files && data.failed_files.length > 0) {
                    const errorDetails = data.failed_files.map((f: any) => `${f.filename}: ${f.error}`).join('; ');
                    throw new Error(`Failed to upload ${base64Images[i].filename}: ${errorDetails}`);
                }

                if (!data.uploaded_files || data.uploaded_files.length === 0) {
                    throw new Error(`Failed to upload ${base64Images[i].filename}`);
                }

                allUrls.push(data.uploaded_files[0].url);
                allThumbnailUrls.push(data.uploaded_files[0].thumbnail_url || data.uploaded_files[0].url);
            }

            return { urls: allUrls, thumbnailUrls: allThumbnailUrls };
        } catch (error: any) {
            console.error('Upload error details:', {
                message: error.message,
                code: error.code,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                config: {
                    url: error.config?.url,
                    method: error.config?.method,
                    timeout: error.config?.timeout,
                    headers: error.config?.headers
                },
                stack: error.stack
            });

            // Detect mobile device for better error messages
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

            // Check for network connectivity issues
            if (!navigator.onLine) {
                throw new Error('No internet connection. Please check your network and try again.');
            }

            if (error.code === 'ECONNABORTED') {
                const message = isMobile
                    ? 'Upload timeout. Please check your mobile connection and try with smaller images.'
                    : 'Upload timeout. Please try with smaller images or check your connection.';
                throw new Error(message);
            }

            // Enhanced network error detection
            if (error.code === 'NETWORK_ERROR' ||
                error.message?.includes('Network Error') ||
                error.message?.includes('ERR_NETWORK') ||
                error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
                error.message?.includes('ERR_CONNECTION_REFUSED') ||
                error.message?.includes('ERR_CONNECTION_TIMED_OUT') ||
                error.message?.includes('ERR_CONNECTION_RESET') ||
                error.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                error.message?.includes('ERR_TIMED_OUT') ||
                error.message?.includes('ERR_FAILED') ||
                error.message?.includes('Failed to fetch') ||
                error.message?.includes('fetch') ||
                !error.response) {

                // Retry logic for network errors
                if (retryCount < maxRetries) {
                    console.log(`Network error detected, retrying in ${(retryCount + 1) * 2} seconds...`);
                    await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 2000));
                    return uploadImagesToServer(images, retryCount + 1);
                }

                const message = isMobile
                    ? 'Network error. Please check your mobile data/WiFi connection and try again.'
                    : 'Network error. Please check your connection and try again.';
                throw new Error(message);
            }

            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }

            if (error.response?.status === 401) {
                // Clear invalid token and redirect to login
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user_data');
                throw new Error('Your session has expired. Please log in again.');
            }

            if (error.response?.status === 413) {
                const message = isMobile
                    ? 'Images are too large. Please reduce file size or try with fewer images.'
                    : 'Images are too large. Please reduce file size and try again.';
                throw new Error(message);
            }

            if (error.response?.status === 403) {
                throw new Error('Access denied. Please check your permissions.');
            }

            if (error.response?.status === 500) {
                throw new Error('Server error. Please try again later.');
            }

            if (error.message) {
                throw new Error(error.message);
            }

            const message = isMobile
                ? 'Failed to upload images. Please check your mobile connection and try again.'
                : 'Failed to upload images. Please check your connection and try again.';
            throw new Error(message);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate and scroll to first missing field
        const errors: Record<string, boolean> = {
            title: !formData.title.trim(),
            description: !formData.description.trim(),
            unit_type: !String(formData.unit_type || '').trim(),
            monthly_rent: !String(formData.monthly_rent || '').trim(),
            address: !isAddressValid || !formData.address.trim(),
            photos: uploadedImages.length === 0,
            utilities: !(formData.parking || formData.allow_pets || formData.gym || formData.pool || formData.laundry),
            gender_preference: !String(formData.gender_preference || '').trim(),
            lease_dates: !formData.lease_start_date || !formData.lease_end_date,
        };

        const firstInvalidKey = ([
            'title',
            'description',
            'unit_type',
            'monthly_rent',
            'address',
            'utilities',
            'gender_preference',
            'lease_dates',
            'photos',
        ] as const).find((k) => errors[k]);

        if (firstInvalidKey) {
            setLoading(false);
            const errorMessageMap: Record<string, string> = {
                title: 'Please enter a title.',
                description: 'Please enter a description.',
                university_id: 'Please select a university or city.',
                unit_type: 'Please select a unit type.',
                monthly_rent: 'Please enter a monthly rent amount.',
                address: 'Please select an address from the suggestions (not just typed in).',
                utilities: 'Please select at least one utility/amenity.',
                gender_preference: 'Please select a gender preference.',
                lease_dates: 'Please select both a start and end date.',
                photos: 'Please add at least one photo.',
            };
            setError(errorMessageMap[firstInvalidKey] || 'Please complete the required fields.');

            const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
                title: titleRef,
                description: descriptionRef,
                university_id: universityRef,
                unit_type: unitTypeRef,
                monthly_rent: rentRef,
                address: addressInputRef,
                utilities: utilitiesRef,
                gender_preference: genderPreferenceRef,
                lease_dates: leaseDatesRef,
                photos: photosRef,
            };

            setTimeout(() => {
                refMap[firstInvalidKey]?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 50);
            return;
        }

        setLoading(true);
        setError('');

        try {

            // Check authentication more robustly
            const token = localStorage.getItem('auth_token');
            if (!token) {
                setError('Authentication token not found. Please log in again.');
                setLoading(false);
                setTimeout(() => {
                    submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
                return;
            }

            // Verify token is still valid by making a test request
            try {
                const testResponse = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!testResponse.ok) {
                    setError('Your session has expired. Please log in again.');
                    setLoading(false);
                    setTimeout(() => {
                        submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                    return;
                }
            } catch (testError) {
                setError('Unable to verify authentication. Please log in again.');
                setLoading(false);
                setTimeout(() => {
                    submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
                return;
            }

            if (!isAuthenticated && !user) {
                setError('You must be logged in to create a listing');
                setLoading(false);
                setTimeout(() => {
                    submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
                return;
            }

            // Check if user already has 3 or more listings (toward account limit)
            try {
                const listingsResponse = await fetch(`${API_BASE_URL}/api/v1/users/listings?include_unavailable=true`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (listingsResponse.ok) {
                    const listingsData = await listingsResponse.json();
                    const userListings = listingsData.data || [];

                    const activeOrPendingCount = userListings.filter((listing: any) =>
                        listing.verification_status !== 'rejected' &&
                        (listing.verification_status === 'verified' ||
                            listing.verification_status === 'approved' ||
                            listing.verification_status === 'pending' ||
                            listing.verification_status === null ||
                            listing.verification_status === undefined)
                    ).length;

                    if (activeOrPendingCount >= 3) {
                        setError('You can only have a maximum of 3 listings at a time. Delete an existing listing to create a new one.');
                        setLoading(false);
                        setTimeout(() => {
                            submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                        return;
                    }
                }
            } catch (listingsError) {
                // If we can't check listings, allow submission (backend will enforce limit)
                console.warn('Could not check user listings count:', listingsError);
            }

            // Upload images first
            let imageData: { urls: string[], thumbnailUrls: string[] } = { urls: [], thumbnailUrls: [] };
            try {
                const imagesForUpload = (() => {
                    if (
                        primaryImageIndex <= 0 ||
                        primaryImageIndex >= uploadedImages.length
                    ) {
                        return uploadedImages;
                    }
                    const next = [...uploadedImages];
                    const [primary] = next.splice(primaryImageIndex, 1);
                    next.unshift(primary);
                    return next;
                })();

                imageData = await uploadImagesToServer(imagesForUpload);
            } catch (uploadError: any) {
                // Check if it's an authentication error
                if (uploadError.message?.includes('session has expired') || uploadError.message?.includes('Authentication failed')) {
                    setError(`Image upload failed: ${uploadError.message}. Please refresh the page and log in again.`);
                    // Clear the form to prevent further attempts
                    setFormData({
                        title: '',
                        description: '',
                        unit_type: '',
                        address: '',
                        monthly_rent: '',
                        pictures: [],
                        lease_term: [],
                        lease_start_date: '',
                        lease_end_date: '',
                        university_id: '',
                        parking: false,
                        allow_pets: false,
                        gym: false,
                        pool: false,
                        laundry: false,
                        gender_preference: 'no_preference',
                        facebook_url: '',
                        instagram_username: ''
                    });
                    setUploadedImages([]);
                    setUploadedImagePreviews([]);
                    setPrimaryImageIndex(0);
                } else {
                    setError(`Image upload failed: ${uploadError.message || 'Unknown error'}`);
                }
                setTimeout(() => {
                    submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
                return;
            }

            const submitData = {
                ...formData,
                monthly_rent: parseInt(formData.monthly_rent),
                university_id: formData.university_id ? parseInt(formData.university_id) : 0,
                pictures: imageData.urls,
                compressed_pictures: imageData.thumbnailUrls,
                lease_term: 'summer', // Hardcoded as requested
                lease_start_date: formData.lease_start_date,
                lease_end_date: formData.lease_end_date,
                allow_pets: formData.allow_pets ? "true" : "false",
                parking: formData.parking ? "true" : "false",
                gym: formData.gym ? "true" : "false",
                pool: formData.pool ? "true" : "false",
                laundry: formData.laundry ? "true" : "false",
                gender_preference: formData.gender_preference,
                facebook_url: formData.facebook_url?.trim() || null,
                instagram_username: formData.instagram_username?.trim() || null
            };

            const response = await axios.post(API_ENDPOINTS.SUBLEASES.CREATE, submitData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const newListingId = response.data.id as string;

            try {
                await refreshUserProfile();
            } catch (refreshError) {
                console.error('Error refreshing user profile after listing creation:', refreshError);
            }

            navigate(`/listing/${newListingId}?boost_post=1`);

        } catch (error: any) {
            console.error('Error creating listing:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            console.error('Error config:', error.config);

            let errorMessage = 'Failed to create listing';

            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
                errorMessage = 'Network error. Please check your connection and try again.';
            } else if (error.response?.status === 401) {
                errorMessage = 'Authentication failed. Please log in again.';
            } else if (error.response?.status === 403) {
                errorMessage = 'You do not have permission to create listings.';
            } else if (error.response?.status === 422) {
                errorMessage = 'Invalid data provided. Please check your form inputs.';
            } else if (error.response?.status === 500) {
                errorMessage = 'Server error. Please try again later.';
            } else if (!error.response) {
                errorMessage = 'Unable to connect to server. Please check your internet connection.';
            }

            setError(errorMessage);
            setTimeout(() => {
                submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        } finally {
            setLoading(false);
        }
    };


    // Check if user is authenticated
    const isUserAuthenticated = isAuthenticated && user && localStorage.getItem('auth_token');

    if (!isUserAuthenticated) {
        return (
            <>
                <NavBar inverted={true} />
                <div className={`create-page ${isDarkMode ? 'dark-mode' : ''}`}>
                    <div className="create-container">
                        <div className="error-message">
                            ❌ You must be logged in to create a listing. Please log in and try again.
                        </div>
                        <div className="loading">
                            <div className="create-dots-loader" aria-label="Loading" role="status">
                                <span className="create-dots-loader-dot" />
                                <span className="create-dots-loader-dot" />
                                <span className="create-dots-loader-dot" />
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <NavBar inverted={true} />
            <div className={`create-page ${isDarkMode ? 'dark-mode' : ''}`}>
                <div className="create-container">
                    <div className="create-header">
                        <h1>Create New Listing</h1>
                    </div>

                    <form onSubmit={handleSubmit} className="create-form" noValidate>
                        <div ref={titleRef} className="form-group">
                            <label htmlFor="title">Title</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                maxLength={30}
                                placeholder="e.g., Room at the Standard, Room at Urban Place Aparments."
                            />
                            <div className={`character-count ${formData.title.length > 30 ? 'error' :
                                formData.title.length > 25 ? 'warning' : ''
                                }`}>
                                {formData.title.length}/30 characters
                            </div>
                        </div>

                        <div ref={descriptionRef} className="form-group">
                            <label htmlFor="description">Description</label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                maxLength={2000}
                                rows={4}
                                placeholder="Describe your listing..."
                                style={{
                                    minHeight: '100px',
                                    resize: 'none',
                                    overflow: 'hidden'
                                }}
                                onInput={(e) => {
                                    const target = e.target as HTMLTextAreaElement;
                                    target.style.height = 'auto';
                                    target.style.height = `${Math.max(100, target.scrollHeight)}px`;
                                }}
                            />
                            <div className={`character-count ${formData.description.length > 2000 ? 'error' :
                                formData.description.length > 1800 ? 'warning' : ''
                                }`}>
                                {formData.description.length}/2000 characters
                            </div>
                        </div>


                        <div ref={leaseDatesRef} className="form-group form-group-centered">
                            <label>Lease Dates</label>
                            <div className="selected-dates-display">
                                <span className={`dates-pill ${(!formData.lease_start_date || !formData.lease_end_date) ? 'placeholder' : ''}`}>
                                    {formData.lease_start_date && formData.lease_end_date ? (
                                        <>
                                            {(() => {
                                                const d = new Date(formData.lease_start_date + 'T00:00:00');
                                                return `${d.toLocaleDateString('en-US', { month: 'short' })} ${d.getDate()} ${d.getFullYear()}`;
                                            })()}
                                            {' - '}
                                            {(() => {
                                                const d = new Date(formData.lease_end_date + 'T00:00:00');
                                                return `${d.toLocaleDateString('en-US', { month: 'short' })} ${d.getDate()} ${d.getFullYear()}`;
                                            })()}
                                        </>
                                    ) : (
                                        "Select start and end date"
                                    )}
                                </span>
                            </div>
                            <RangeCalendar
                                startDate={formData.lease_start_date}
                                endDate={formData.lease_end_date}
                                onChange={(start, end) => setFormData(prev => ({ ...prev, lease_start_date: start, lease_end_date: end }))}
                            />
                        </div>

                        <div className="form-row">
                            <div ref={unitTypeRef} className="form-group">
                                <label htmlFor="unit_type">Entire Unit Type</label>
                                <select
                                    id="unit_type"
                                    name="unit_type"
                                    value={formData.unit_type}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select unit type</option>
                                    <option value="studio">Studio</option>
                                    <option value="1x1">1x1</option>
                                    <option value="1x2">1x2</option>
                                    <option value="2x1">2x1</option>
                                    <option value="2x2">2x2</option>
                                    <option value="3x1">3x1</option>
                                    <option value="3x2">3x2</option>
                                    <option value="3x3">3x3</option>
                                    <option value="4x2">4x2</option>
                                    <option value="4x3">4x3</option>
                                    <option value="4x4">4x4</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div ref={rentRef} className="form-group">
                                <label htmlFor="monthly_rent">Monthly Rent ($)</label>
                                <input
                                    type="number"
                                    id="monthly_rent"
                                    name="monthly_rent"
                                    value={formData.monthly_rent}
                                    onChange={handleInputChange}
                                    required
                                    min="1"
                                    placeholder="800"
                                />
                            </div>
                        </div>

                        <div className="form-group" ref={addressInputRef}>
                            <label htmlFor="address">Address</label>
                            <AddressAutocomplete
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={(value) => setFormData(prev => ({ ...prev, address: value }))}
                                onValidationChange={(isValid) => {
                                    setIsAddressValid(isValid);
                                }}
                                required
                                maxLength={300}
                                placeholder="Enter Address ..."
                            />
                        </div>

                        <div ref={universityRef} className="form-group">
                            <label htmlFor="university_id">University (Optional)</label>
                            <UniversitySearch
                                value={formData.university_id}
                                onChange={(value) => setFormData(prev => ({ ...prev, university_id: value }))}
                                placeholder="Search University e.g. USF (Optional)"
                                className="create-page-university-search"
                                disableCapsuleClick={true}
                                supportedUniversityIds={SUPPORTED_UNIVERSITY_IDS}
                                disableCapsule={true}
                                showCities={false}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="facebook_url">Facebook Link (Optional)</label>
                            <input
                                type="url"
                                id="facebook_url"
                                name="facebook_url"
                                value={formData.facebook_url || ''}
                                onChange={handleInputChange}
                                placeholder="e.g. https://www.facebook.com/marketplace/item/12345"
                                maxLength={500}
                            />
                        </div>

                        {!user?.instagram && (
                            <div className="form-group">
                                <label htmlFor="instagram_username">Instagram Username (Optional)</label>
                                <input
                                    type="text"
                                    id="instagram_username"
                                    name="instagram_username"
                                    value={formData.instagram_username || ''}
                                    onChange={handleInputChange}
                                    placeholder="e.g. usublease"
                                    maxLength={50}
                                />
                            </div>
                        )}

                        <div className="form-group utilities-section">
                            <label className="section-label">Utilities</label>
                            <div
                                ref={utilitiesRef}
                                className="utilities-checkboxes"
                            >
                                {/* Row 1: Parking & Laundry */}
                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="parking"
                                            checked={formData.parking}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Parking</span>
                                    </label>
                                </div>

                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="laundry"
                                            checked={formData.laundry}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Laundry</span>
                                    </label>
                                </div>

                                {/* Row 2: Pets, Pool, Gym */}
                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="allow_pets"
                                            checked={formData.allow_pets}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Pets</span>
                                    </label>
                                </div>

                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="pool"
                                            checked={formData.pool}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Pool</span>
                                    </label>
                                </div>

                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="gym"
                                            checked={formData.gym}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Gym</span>
                                    </label>
                                </div>
                            </div>

                            <div ref={genderPreferenceRef} className="gender-preference-section">
                                <label htmlFor="gender_preference" className="gender-label">Gender Preference</label>
                                <select
                                    id="gender_preference"
                                    name="gender_preference"
                                    value={formData.gender_preference}
                                    onChange={handleInputChange}
                                    className="gender-select"
                                >
                                    <option value="no_preference">No Gender Preference</option>
                                    <option value="male_preferred">Males</option>
                                    <option value="female_preferred">Females</option>
                                </select>
                            </div>
                        </div>

                        <div ref={photosRef} className="form-group photo-upload-section">
                            <label>Photos (Max 20)</label>
                            <p className="photo-instruction">Please ensure the first photo is of the room and NOT the layout.</p>
                            <div className="photo-upload-area">
                                <input
                                    type="file"
                                    id="photo-upload"
                                    accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/tiff,image/bmp,image/gif"
                                    multiple
                                    onChange={handleImageUpload}
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="photo-upload" className="upload-button">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Add Photos
                                </label>
                            </div>

                            {uploadedImages.length > 0 && (
                                <div className="uploaded-images">
                                    <div className="images-grid">
                                        {uploadedImages.map((image, index) => (
                                            <div
                                                key={index}
                                                className="image-preview"
                                                onClick={() => setPrimaryImage(index)}
                                                title="Click to set as primary photo"
                                            >
                                                {index === primaryImageIndex && (
                                                    <div className="primary-tile-label" aria-hidden="true">Primary</div>
                                                )}
                                                <img
                                                    src={uploadedImagePreviews[index]}
                                                    alt={`Preview ${index + 1}`}
                                                />
                                                {index !== primaryImageIndex && (
                                                    <div className="image-controls">
                                                        <button
                                                            type="button"
                                                            className="remove-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeImage(index);
                                                            }}
                                                            title="Remove photo"
                                                            aria-label="Remove photo"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                <line x1="6" y1="6" x2="18" y2="18" />
                                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <p className="photos-count">{uploadedImages.length}/20 photos uploaded</p>
                                </div>
                            )}
                        </div>

                        {error && (
                            <p style={{ color: '#d32f2f', fontSize: '0.875rem', marginBottom: '0.5rem', marginTop: '0', textAlign: 'center' }}>
                                {error}
                            </p>
                        )}
                        <div className="form-actions" ref={submitButtonRef}>
                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="submit-btn-dots" aria-label="Creating listing" role="status">
                                        <span className="submit-btn-dot" />
                                        <span className="submit-btn-dot" />
                                        <span className="submit-btn-dot" />
                                    </span>
                                ) : (
                                    'Create Listing'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Create;
